import { useEffect } from 'react'

import { useAsyncTask } from '../../../../hooks'
import { isDocFlowSplittingType } from '../../DocFlow.utils.mapping'
import {
  DocFlowMappingProcessCell,
  getDocFlowMappingWidgetSplittingArea,
} from '../../DocFlowMapping'
import { isPagesOverlimit, updateUploadingItem } from '../../DocFlowUploading'
import { getDocFlowMappingTypeWidgetNotifications } from '../../DocFlowWidgets'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowMappingContext } from '../useDocFlowMappingContext'

import {
  USE_DOC_FLOW_MAPPING_TYPE_WIDGET_CHECK_PROCESS_STATUS_ID,
  USE_DOC_FLOW_MAPPING_TYPE_WIDGET_CHECK_PROCESS_STATUS_INTERVAL,
} from './useDocFlowMappingWidgetType.constants'
import {
  getUpdateFileProcessPayload,
  isDocFlowMappingWidgetDone,
  isDocFlowMappingWidgetMultiplyingDone,
  isDocFlowMappingWidgetSplittingDone,
  isDocFlowMappingWidgetTypeInProcess,
} from './useDocFlowMappingWidgetType.utils'

export function useDocFlowMappingWidgetType({ itemIndex }) {
  const { documentId, handleReloadDocFlow, phaseDisabled } = useDocFlowContext()

  const {
    activeFile,
    handleClickNextWidget,
    handleInitSelectArea,
    handleReloadFileProcess,
    handleReloadUploadingFilesAll,
    handleUpdateFileProcess,
    processData,
    setPageLoader,
  } = useDocFlowMappingContext()

  const { asyncTasks, handleAddAsyncTask, handleRemoveAsyncTask } =
    useAsyncTask()

  const isSplitting = isDocFlowSplittingType(activeFile?.type)

  const isMultiplyingDone = isDocFlowMappingWidgetMultiplyingDone({
    isSplitting,
    status: activeFile?.status,
  })

  const isSplittingTypeDone = isDocFlowMappingWidgetSplittingDone({
    isSplitting,
    processData,
  })

  // multiplying type or splitting and task is finished in aws
  const widgetDone = isDocFlowMappingWidgetDone({
    isMultiplyingDone,
    isSplittingTypeDone,
  })

  // check splitting type and file is analized in aws
  const typeInProcess = isDocFlowMappingWidgetTypeInProcess({
    isSplitting,
    processData,
  })

  function handleSubmitChangeType(value) {
    setPageLoader(true)

    updateUploadingItem({
      data: {
        type: value.value,
      },
      id: documentId,
      itemId: activeFile.itemId,
    }).then(() => {
      Promise.all([
        handleReloadUploadingFilesAll(),
        handleReloadFileProcess(),
        handleReloadDocFlow(),
      ]).then(() => {
        setPageLoader(false)
      })
    })
  }

  function handleInitSplittingSelectArea() {
    const areaValue = getDocFlowMappingWidgetSplittingArea(processData)

    handleInitSelectArea(areaValue)
  }

  function handleChangeActivePhase(data) {
    if (data[DocFlowMappingProcessCell.AwsProcessFinishedAt]) {
      handleClickNextWidget(itemIndex)
      handleInitSplittingSelectArea()
    }
  }

  function handleClickNext() {
    // user returned from another widget. Just change active widget
    if (widgetDone || phaseDisabled) {
      handleClickNextWidget(itemIndex)

      return
    }

    handleUpdateFileProcess(getUpdateFileProcessPayload(processData)).then(
      data => {
        handleReloadUploadingFilesAll()

        handleChangeActivePhase(data)
      }
    )
  }

  useEffect(() => {
    // check while file isn't be analized in aws
    if (typeInProcess) {
      handleAddAsyncTask({
        id: USE_DOC_FLOW_MAPPING_TYPE_WIDGET_CHECK_PROCESS_STATUS_ID,
        interval: setInterval(() => {
          handleReloadFileProcess().then(handleChangeActivePhase)
        }, USE_DOC_FLOW_MAPPING_TYPE_WIDGET_CHECK_PROCESS_STATUS_INTERVAL),
      })
    } else if (asyncTasks.length > 0) {
      handleRemoveAsyncTask(
        USE_DOC_FLOW_MAPPING_TYPE_WIDGET_CHECK_PROCESS_STATUS_ID
      )
    }
  }, [typeInProcess])

  const notification = getDocFlowMappingTypeWidgetNotifications({
    isMultiplyingDone,
    processData,
    typeInProcess,
  })

  return {
    // disable fields when user returned from  another widget
    disabled: phaseDisabled || isSplittingTypeDone,
    handleClickNext,
    handleSubmitChangeType,
    isMultiplyingDone,
    isPagesOverlimit: isPagesOverlimit(activeFile),
    nextButtonDisabled: phaseDisabled && !widgetDone,
    notification,
    processData,
    typeInProcess,
    widgetDone,
  }
}
